import { createContext, useCallback, useContext, useState, useMemo, useRef } from 'react';
import { ConfirmDialog } from '../components/Modal';
import { useT } from '../translation/src';
import { GroupType } from '../types/graphqlTypes';
import { AnnotationType } from '@pointorama/potree-shared';

/** Any type of item that can be deleted — groups and annotations.
 *  Uses the raw annotation enum (not icon keys) so OPENING is a first-class member. */
export type DeletableType = GroupType | AnnotationType;

/**
 * Types that require a confirmation dialog before deletion.
 * Add or remove entries here to control which types prompt the user.
 * For now every type is confirmed — when a recycle bin is added, this can be narrowed down.
 */
const TYPES_REQUIRING_CONFIRMATION: ReadonlySet<DeletableType> = new Set<DeletableType>([
  GroupType.Default,
  GroupType.Floor,
  GroupType.Surface,
  GroupType.Room,
  AnnotationType.POINT,
  AnnotationType.DISTANCE,
  AnnotationType.AREA,
  AnnotationType.BOX,
  AnnotationType.POINTCLUSTER,
  AnnotationType.ROOM,
  AnnotationType.WALL_SKETCH,
  AnnotationType.OPENING,
]);

interface DeleteConfirmationContextValue {
  /** Show the confirm dialog; `message` overrides the generic body text
   *  (e.g. the wall-sketch cascade warning). */
  requestDeleteConfirmation: (onConfirm: () => void, message?: string) => void;
  /** Check if the given type requires confirmation before delete */
  requiresConfirmation: (deletableType: DeletableType | undefined) => boolean;
}

const DeleteConfirmationContext = createContext<DeleteConfirmationContextValue | null>(null);

export const DeleteConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [messageOverride, setMessageOverride] = useState<string | null>(null);

  // Store onConfirm in a ref so it persists across renders and doesn't cause stale closures
  const onConfirmRef = useRef<(() => void) | null>(null);

  // Translations
  const deleteTitle = useT('Delete', { swc: true });
  const deleteMessage = useT('Are you sure you want to delete this?');

  const requestDeleteConfirmation = useCallback((onConfirm: () => void, message?: string) => {
    onConfirmRef.current = onConfirm;
    setMessageOverride(message ?? null);
    setOpen(true);
  }, []);

  const requiresConfirmation = useCallback((deletableType: DeletableType | undefined) => {
    if (!deletableType) return false;
    return TYPES_REQUIRING_CONFIRMATION.has(deletableType);
  }, []);

  const handleClose = useCallback(() => {
    onConfirmRef.current = null;
    setOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    if (onConfirmRef.current) {
      onConfirmRef.current();
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      requestDeleteConfirmation,
      requiresConfirmation,
    }),
    [requestDeleteConfirmation, requiresConfirmation],
  );

  return (
    <DeleteConfirmationContext.Provider value={contextValue}>
      {children}
      <ConfirmDialog open={open} onClose={handleClose} onConfirm={handleConfirm} title={deleteTitle} message={messageOverride ?? deleteMessage} variant="danger" />
    </DeleteConfirmationContext.Provider>
  );
};

export const useDeleteConfirmation = () => {
  const context = useContext(DeleteConfirmationContext);
  if (!context) {
    throw new Error('useDeleteConfirmation must be used within a DeleteConfirmationProvider');
  }
  return context;
};
